import { useEffect, useState } from 'react';
import { useApi, type MeResponse } from '@/lib/api';
import { useMe } from '@/lib/useMe';
import { MeGate } from '@/components/MeGate';
import { useToast } from '@/components/Toast';
import { Save, Plus, Shield } from 'lucide-react';

interface OrgDomainRow {
  id: string;
  domain: string;
  status: 'pending' | 'verified' | 'disabled';
  verified_at: number | null;
  created_at: number;
}

export function Settings() {
  const meStatus = useMe();
  return <MeGate status={meStatus}>{(me) => <SettingsBody me={me} />}</MeGate>;
}

function SettingsBody({ me }: { me: MeResponse }) {
  const orgId = me.org!.id;
  const api = useApi();
  const toast = useToast();
  const [threshold, setThreshold] = useState(me.org!.ai_auto_send_threshold);
  const [tone, setTone] = useState(me.org!.ai_tone);
  const [signature, setSignature] = useState(me.org!.ai_signature ?? '');
  const [policy, setPolicy] = useState(me.org!.ai_policy ?? '');
  // email_prefix stores "orghandle-role" e.g. "cyreva-support"
  const existingPrefix = me.org!.email_prefix ?? '';
  const dashIdx = existingPrefix.indexOf('-');
  const [orgHandle, setOrgHandle] = useState(dashIdx > 0 ? existingPrefix.slice(0, dashIdx) : existingPrefix);
  const [emailRole, setEmailRole] = useState(dashIdx > 0 ? existingPrefix.slice(dashIdx + 1) : '');
  const emailPrefix = orgHandle ? (emailRole ? `${orgHandle}-${emailRole}` : orgHandle) : '';
  const [saving, setSaving] = useState(false);

  const [domains, setDomains] = useState<OrgDomainRow[]>([]);
  const [newDomain, setNewDomain] = useState('');

  useEffect(() => {
    void api.get<{ domains: OrgDomainRow[] }>(`/api/orgs/${orgId}/domains`)
      .then((r) => setDomains(r.domains));
  }, [api, orgId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/orgs/${orgId}/settings`, {
        aiAutoSendThreshold: threshold,
        aiTone: tone,
        aiSignature: signature,
        aiPolicy: policy,
        emailPrefix: emailPrefix,
      });
      toast.success('Settings saved');
    } catch (err) {
      const data = (err as Error & { data?: { error?: string; detail?: string } }).data;
      if (data?.error === 'prefix_taken') {
        toast.error('Email prefix unavailable', data.detail ?? 'That prefix is already taken by another organization.');
      } else {
        toast.error('Save failed', (err as Error).message);
      }
    } finally { setSaving(false); }
  };

  const addDomain = async () => {
    if (!newDomain.trim()) return;
    try {
      const { id } = await api.post<{ id: string; domain: string; status: string }>(
        `/api/orgs/${orgId}/domains`, { domain: newDomain.trim() },
      );
      setDomains([...domains, { id, domain: newDomain.trim(), status: 'pending', verified_at: null, created_at: Date.now() }]);
      setNewDomain('');
      toast.success('Domain added', 'Set the MX records shown, then click Verify.');
    } catch (err) {
      toast.error('Could not add domain', (err as Error).message);
    }
  };

  const verify = async (domainId: string) => {
    try {
      const { status, detail } = await api.post<{ status: string; detail?: string }>(
        `/api/orgs/${orgId}/domains/${domainId}/verify`,
        {},
      );
      setDomains(domains.map((d) =>
        d.id === domainId
          ? { ...d, status: status as OrgDomainRow['status'], verified_at: status === 'verified' ? Date.now() : d.verified_at }
          : d,
      ));
      if (status === 'verified') toast.success('Domain verified');
      else toast.error('Domain not verified yet', detail ?? 'MX records missing or not pointing at Cloudflare.');
    } catch (err) {
      toast.error('Verify failed', (err as Error).message);
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <header className="p-6 border-b bg-background">
        <h1 className="text-xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Organization-wide AI behavior, branding, and inbound domains.
        </p>
      </header>
      <div className="p-6 space-y-6 max-w-3xl">
        <section className="card p-5 space-y-3">
          <h2 className="font-semibold">Email identity</h2>
          <p className="text-sm text-muted-foreground">
            Set your organization's email address. Your org name is used as a prefix
            so every org gets its own namespace (e.g. <code className="px-1 py-0.5 bg-muted rounded text-xs">cyreva-support@edgemail.cloud</code>).
          </p>
          <div className="space-y-2">
            <label className="block text-sm">
              Organization handle
              <input className="input mt-1" value={orgHandle}
                     onChange={(e) => setOrgHandle(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 30))}
                     placeholder="cyreva" />
              <div className="text-xs text-muted-foreground mt-1">
                Short name for your org. Letters and numbers only.
              </div>
            </label>
            <label className="block text-sm">
              Email name
              <input className="input mt-1" value={emailRole}
                     onChange={(e) => setEmailRole(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 30))}
                     placeholder="support" />
              <div className="text-xs text-muted-foreground mt-1">
                The role or function — e.g. support, help, billing.
              </div>
            </label>
          </div>
          {emailPrefix && (
            <div className="rounded-md border border-border bg-background px-3 py-2">
              <div className="text-xs text-muted-foreground mb-1">Your email address will be</div>
              <div className="font-mono text-sm font-medium text-foreground">{emailPrefix}@edgemail.cloud</div>
            </div>
          )}
          <button className="btn-primary" onClick={save} disabled={saving}>
            <Save className="h-3.5 w-3.5" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </section>

        <section className="card p-5 space-y-3">
          <h2 className="font-semibold">AI behavior</h2>
          <label className="block text-sm">
            Auto-send threshold
            <input
              type="range" min={0} max={1} step={0.01}
              value={threshold}
              onChange={(e) => setThreshold(parseFloat(e.target.value))}
              className="w-full mt-1"
            />
            <div className="text-xs text-muted-foreground mt-1">
              The agent auto-sends replies when its confidence ≥{' '}
              <b>{(threshold * 100).toFixed(0)}%</b>. Lower = more automation, higher = more review.
            </div>
          </label>
          <label className="block text-sm">
            Tone
            <input className="input mt-1" value={tone} onChange={(e) => setTone(e.target.value)}
                   placeholder="friendly-concise / formal / playful" />
          </label>
          <label className="block text-sm">
            Signature
            <input className="input mt-1" value={signature} onChange={(e) => setSignature(e.target.value)}
                   placeholder='— The Acme team' />
          </label>
          <label className="block text-sm">
            Policy
            <textarea className="input mt-1 min-h-[100px]" value={policy} onChange={(e) => setPolicy(e.target.value)}
                      placeholder="House rules fed to every reply — e.g. 'Never promise refunds; escalate billing disputes.'" />
          </label>
          <button className="btn-primary" onClick={save} disabled={saving}>
            <Save className="h-3.5 w-3.5" />
            {saving ? 'Saving…' : 'Save'}
          </button>
        </section>

        <section className="card p-5 space-y-3">
          <h2 className="font-semibold">Inbound addresses</h2>
          <p className="text-sm text-muted-foreground">
            Default: <code className="px-1 py-0.5 bg-muted rounded">{me.org!.slug}@edgemail.cloud</code>.
            Add a custom domain below; point its MX records to Cloudflare, then click Verify.
          </p>
          <div className="flex gap-2">
            <input className="input" value={newDomain} onChange={(e) => setNewDomain(e.target.value)}
                   placeholder="support.acme.com" />
            <button className="btn-secondary" onClick={addDomain}>
              <Plus className="h-3.5 w-3.5" />
              Add
            </button>
          </div>
          {domains.length === 0 ? (
            <p className="text-xs text-muted-foreground">No custom domains yet.</p>
          ) : (
            <ul className="space-y-1.5 mt-2">
              {domains.map((d) => (
                <li key={d.id} className="flex items-center justify-between border rounded-md px-3 py-2">
                  <span className="font-mono text-sm">{d.domain}</span>
                  <span className="flex items-center gap-2">
                    <span className={
                      'badge ' + (d.status === 'verified'
                        ? 'bg-emerald-100 text-emerald-900 border-emerald-200'
                        : 'bg-amber-100 text-amber-900 border-amber-200')
                    }>
                      <Shield className="h-3 w-3 mr-1" />
                      {d.status}
                    </span>
                    {d.status !== 'verified' && (
                      <button className="btn-ghost" onClick={() => verify(d.id)}>Verify</button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
